import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, map, Observable, Subject, takeUntil } from 'rxjs';
import { MarkerDisplayModule } from '@go-modules/components/marker-display/marker-display.module';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { Activity } from '@go-modules/interfaces/activity';
import { BaseMarker, MarkerSet } from '@go-modules/interfaces/markers/marker';
import { RubricPreviewTemplateElement, RubricTemplate } from '@go-modules/interfaces/rubrics/rubric';
import { Task } from '@go-modules/interfaces/task';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { LibraryService } from '@go-modules/services/library/library.service';
import { NgxTagSetService } from '@go-modules/services/tag-set/ngx-tag-set.service';
import { mediaToken } from 'go-modules/models/media/media.factory';
import type { MediaFactory, MediaSchema } from 'go-modules/models/media/media-schema.interface';
import { rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import type { Media } from 'go-modules/services/attachment/media';
import { GoDialogContentDirective } from '@go-modules/directives/go-dialog-content/go-dialog-content.directive';
import { MatIconModule } from '@angular/material/icon';

type ActivityPreviewSection = 'details' | 'tasks' | 'rubrics' | 'markers' | 'ownerAttachments';

interface ActivityPreviewSectionOption {
	value: ActivityPreviewSection;
	label: string;
}

interface RubricPreview {
	id: number;
	heading: string;
	title: string;
	total: number | string | null;
	groups: RubricPreviewGroup[];
}

interface RubricPreviewItem {
	id: number;
	title: string;
	total: number;
}

interface RubricPreviewGroup {
	id: string;
	title: string | null;
	total: number | null;
	items: RubricPreviewItem[];
}

interface MediaPreviewItem {
	media: MediaSchema;
	title: string;
}

interface MediaPreviewMetadata {
	title?: string;
	filename?: string;
	filename_original?: string;
}

@Component({
	selector: 'activity-preview-dialog',
	standalone: true,
	imports: [
		GoDialogContentDirective,
		A11yModule,
		CommonModule,
		MatFormFieldModule,
		MatSelectModule,
		MatIconModule,
		MarkerDisplayModule,
		NgxMediaThumbnailModule,
		TranslateModule
	],
	templateUrl: './activity-preview-dialog.component.html',
	styleUrls: ['./activity-preview-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityPreviewDialogComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();
	private readonly dialogRef = inject(GoDialogRef);
	private readonly activityService = inject(NgxActivityService);
	private readonly tagSetService = inject(NgxTagSetService);
	private readonly libraryService = inject(LibraryService);
	private readonly translate = inject(TranslateService);
	private readonly rubricEditorModal = inject(rubricEditorModalToken, { optional: true });
	private readonly MediaModel = inject<MediaFactory>(mediaToken);
	private readonly userService = inject<UserService>(userServiceToken);
	private readonly previewCreationDate = new Date();

	public readonly dialogTitleId = 'activity-preview-dialog-title';
	public readonly sectionSelectId = 'activity-preview-dialog-section-select';
	public readonly activity = this.data.activity;
	public readonly tasks = this.data.tasks;
	public readonly selectedSection = signal<ActivityPreviewSection>('details');
	public readonly markerSet = signal<MarkerSet | null>(null);
	public readonly rubrics = signal<RubricPreview[]>([]);
	public readonly sections: ActivityPreviewSectionOption[] = [
		{ value: 'details', label: 'Activity Details' },
		{ value: 'tasks', label: 'Activity Tasks' },
		{ value: 'rubrics', label: 'Rubrics' },
		{ value: 'markers', label: 'Markers' },
		{ value: 'ownerAttachments', label: 'Owner Attachments' }
	];
	public readonly selectedSectionLabel = computed(() =>
		this.sections.find((section) => section.value === this.selectedSection())?.label
	);
	public readonly activePanelId = computed(() =>
		`activity-preview-dialog-${this.selectedSection()}-panel`
	);

	constructor (
		@Inject(GO_MODAL_DATA) public data: {
			activity: Activity;
			tasks: Task[];
		}
	) {}

	public ngOnInit (): void {
		this.loadMarkerSet();
		this.loadRubrics();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public close (): void {
		this.dialogRef.close();
	}

	public selectSection (section: ActivityPreviewSection): void {
		this.selectedSection.set(section);
	}

	public onSectionSelect (change: MatSelectChange): void {
		this.selectSection(change.value as ActivityPreviewSection);
	}

	public isSectionSelected (section: ActivityPreviewSection): boolean {
		return this.selectedSection() === section;
	}

	public taskTitle (task: Task): string {
		return this.activityService.getTaskTitleAndDescription(this.activity, task)?.title;
	}

	public taskInstructionText (task: Task): string | null {
		const text = task.instruction?.text;
		return text == null || text === '' ? null : text;
	}

	public taskInstructionMedia (task: Task): MediaPreviewItem | null {
		const media = task.instruction?.media as Media | null | undefined;
		return media == null ? null : this.toMediaPreviewItem(media);
	}

	public activityTypeLabel (): string {
		return this.resolveActivityTypeLabel();
	}

	public creatorName (): string | undefined {
		const ownerName = this.activity.owner_name;
		return ownerName == null || ownerName === ''
			? this.userService.currentUser?.full_name
			: ownerName;
	}

	public creationDate (): string | Date {
		const activity = this.activity as Activity & { created_at?: string | null };
		const createdAt = activity.created_at;
		return createdAt == null || createdAt === '' ? this.previewCreationDate : createdAt;
	}

	public aiMarkers (): BaseMarker[] {
		return (this.activity.llm_definitions)
			.map((definition) => definition.tags?.[0])
			.filter((marker): marker is BaseMarker => marker != null);
	}

	public ownerMediaItems (): MediaPreviewItem[] {
		return this.activity.attachments.map((attachment) => this.toMediaPreviewItem(attachment.media));
	}

	public mediaTitle (media: MediaPreviewMetadata): string {
		if (media.title != null && media.title !== '') {
			return media.title;
		}

		if (media.filename != null && media.filename !== '') {
			return media.filename;
		}

		if (media.filename_original != null && media.filename_original !== '') {
			return media.filename_original;
		}
	}

	public previewRubric (rubric: RubricPreview): void {
		this.rubricEditorModal?.open({
			modalData: {
				options: {
					schemaId: rubric.id,
					mode: 'print',
					previewOnly: true
				}
			}
		});
	}

	public sectionPanelId (section: ActivityPreviewSection): string {
		return `activity-preview-dialog-${section}-panel`;
	}

	private resolveActivityTypeLabel (): string {
		if (this.activity.is_conversation && this.activity.has_response_media) {
			return this.activity.live_session_enabled
				? this.translate.instant('common_standard-group-with-live-review')
				: this.translate.instant('common_standard-group');
		}

		if (this.activity.live_session_enabled) {
			return this.activity.template?.slug === 'stimulus_response'
				? this.translate.instant('common_stimulus-with-live-review')
				: this.translate.instant('common_standard-with-live-review');
		}

		return this.activity.template.type;
	}

	private loadMarkerSet (): void {
		if (this.activity.tag_set_id == null || this.activity.tag_set_id <= 0) {
			this.markerSet.set(null);
			return;
		}

		this.tagSetService.getById(this.activity.tag_set_id)
			.pipe(
				takeUntil(this.destroy$)
			)
			.subscribe((markerSet) => this.markerSet.set(markerSet));
	}

	private loadRubrics (): void {
		const rubricRequests: Observable<RubricPreview | null>[] = [];

		if (this.activity.rubric_template_id != null && this.activity.rubric_template_id > 0) {
			rubricRequests.push(
				this.loadRubric(this.activity.rubric_template_id, 'Scoring Rubric')
			);
		}

		if (this.activity.peer_rubric_id != null && this.activity.peer_rubric_id > 0) {
			rubricRequests.push(
				this.loadRubric(this.activity.peer_rubric_id, 'Participant Rubric')
			);
		}

		if (rubricRequests.length === 0) {
			this.rubrics.set([]);
			return;
		}

		forkJoin(rubricRequests)
			.pipe(takeUntil(this.destroy$))
			.subscribe((rubrics) => {
				this.rubrics.set(
					rubrics.filter((rubric): rubric is RubricPreview => rubric != null)
				);
			});


	}

	private loadRubric (rubricId: number, heading: string): Observable<RubricPreview | null> {
		return this.libraryService.getRubricTemplate(rubricId).pipe(
			map((rubricTemplate) => this.toRubricPreview(rubricTemplate, heading))
		);
	}

	private toRubricPreview (rubricTemplate: RubricTemplate, heading: string): RubricPreview {
		return {
			id: rubricTemplate.id,
			heading,
			title: rubricTemplate.title,
			total: rubricTemplate.total,
			groups: this.toRubricPreviewGroups(rubricTemplate.elements)
		};
	}

	private toRubricPreviewGroups (elements: RubricPreviewTemplateElement[]): RubricPreviewGroup[] {
		const groups: RubricPreviewGroup[] = [];
		let currentGroup: RubricPreviewGroup | null = null;

		elements.forEach((element) => {
			if (element.type === 'category') {
				currentGroup = {
					id: `category-${element.id}`,
					title: element.title ?? '',
					total: 0,
					items: []
				};
				groups.push(currentGroup);
				return;
			}

			if (element.type === 'break') {
				currentGroup = null;
				return;
			}

			// Group consecutive criteria that are not contained in a category.
			if (currentGroup == null) {
				currentGroup = {
					id: `criteria-${element.id}`,
					title: null,
					total: null,
					items: []
				};
				groups.push(currentGroup);
			}

			const item = this.toRubricPreviewItem(element);
			currentGroup.items.push(item);
			if (currentGroup.total != null) {
				currentGroup.total += item.total;
			}
		});

		groups.forEach((group) => {
			if (group.total != null) {
				group.total = Number(group.total.toFixed(2));
			}
		});

		return groups;
	}

	private toRubricPreviewItem (element: RubricPreviewTemplateElement): RubricPreviewItem {
		return {
			id: element.id,
			title: element.title ?? element.label ?? '',
			total: this.rubricItemTotal(element)
		};
	}

	private rubricItemTotal (element: RubricPreviewTemplateElement): number {
		if (element.type === 'points') {
			return Number(element.total ?? 0);
		}

		if (element.type === 'checkbox') {
			return Number(element.value ?? 0);
		}

		if ((element.options?.length ?? 0) > 0) {
			return Math.max(0, ...(element.options ?? []).map((option) => Number(option.value)));
		}

		return 0;
	}

	private ensureMediaInstance (media: Media | MediaSchema): MediaSchema {
		if (media instanceof (this.MediaModel as any)) {
			return media as MediaSchema;
		}

		return this.MediaModel.model(media as Partial<MediaSchema>);
	}

	private toMediaPreviewItem (media: Media | MediaSchema): MediaPreviewItem {
		const mediaInstance = this.ensureMediaInstance(media);
		return {
			media: mediaInstance,
			title: this.mediaTitle(mediaInstance)
		};
	}
}
